import type { ComponentType } from 'react'
import {
  Activity,
  Bookmark,
  Download,
  HelpCircle,
  History,
  Inbox,
  Layers,
  LayoutDashboard,
  Search,
  Star,
  TrendingDown,
} from 'lucide-react'

type Icon = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>

export interface NavPunkt {
  to: string
  label: string
  icon: Icon
  /** Nur fuer Admins sichtbar -- Betriebsinterna und noch leere Seiten. */
  nurAdmin?: boolean
  /** Erst nach der Anmeldung sichtbar. */
  nurAngemeldet?: boolean
  /** Aktiv nur bei exaktem Pfad (fuer "/"). */
  ende?: boolean
}

export interface NavGruppe {
  titel: string
  punkte: NavPunkt[]
}

/**
 * Hauptnavigation, abgestimmt mit Kim (12.09.2026).
 *
 * "Modell" ist vorerst Admin-Sicht: Die Seiten stehen, der Inhalt folgt --
 * Kunden sollen keine Baustellen sehen. "Daten" bleibt dauerhaft Admin-Sicht:
 * Abo-Status und Importe sind fuer zahlende Kunden irrelevant.
 *
 * Besucher sehen nur Start und Glossar; Screener, Top-Ideen und Watchlist
 * erscheinen erst nach der Anmeldung (Kim, 13.09.2026).
 */
export const NAVIGATION: NavGruppe[] = [
  { titel: 'Übersicht', punkte: [{ to: '/', label: 'Start', icon: LayoutDashboard, ende: true }] },
  {
    titel: 'Analyse',
    punkte: [
      { to: '/screener', label: 'Screener', icon: Search, ende: true, nurAngemeldet: true },
      { to: '/ideen', label: 'Top-Ideen', icon: Star, nurAngemeldet: true },
      { to: '/unterbewertet', label: 'Unterbewertet', icon: TrendingDown, nurAngemeldet: true },
      { to: '/watchlist', label: 'Watchlist', icon: Bookmark, nurAngemeldet: true },
    ],
  },
  {
    titel: 'Modell',
    punkte: [
      { to: '/rueckvergleich', label: 'Rückvergleich', icon: History, nurAdmin: true },
      { to: '/groessen-split', label: 'Größen-Split', icon: Layers, nurAdmin: true },
    ],
  },
  {
    titel: 'Daten',
    punkte: [
      { to: '/importe', label: 'Importe', icon: Download, nurAdmin: true },
      { to: '/anfragen', label: 'Anfragen', icon: Inbox, nurAdmin: true },
      { to: '/status', label: 'Systemstatus', icon: Activity, nurAdmin: true },
    ],
  },
  { titel: 'Hilfe', punkte: [{ to: '/glossar', label: 'Glossar', icon: HelpCircle }] },
]

/**
 * Seiten, die es fuer Nicht-Admins nicht gibt (s. Zugang.tsx) -- auch der
 * Titel oben verraet sie nicht. Dazu die verbliebene alte Seite: der
 * Kennzahlen-Filter. Die Einzelansicht je Aktie (/aktie/...) ist seit
 * 15.09.2026 fuer alle da und stuft selbst ab.
 */
const NUR_ADMIN_PFADE = ['/rueckvergleich', '/groessen-split', '/importe', '/anfragen', '/status', '/screener/kennzahlen']

function normalisiert(pfad: string): string {
  return pfad.length > 1 ? pfad.replace(/\/+$/, '') : pfad
}

export function istAdminPfad(pfad: string): boolean {
  const norm = normalisiert(pfad)
  return NUR_ADMIN_PFADE.includes(norm)
}

const TITEL: Record<string, string> = {
  '/': 'Start',
  '/screener': 'Screener',
  '/screener/kennzahlen': 'Kennzahlen-Filter',
  '/ideen': 'Top-Ideen',
  '/unterbewertet': 'Unterbewertete Aktien',
  '/watchlist': 'Watchlist',
  '/rueckvergleich': 'Rückvergleich',
  '/groessen-split': 'Größen-Split',
  '/importe': 'Importe',
  '/anfragen': 'Anfragen',
  '/status': 'Systemstatus',
  '/glossar': 'Glossar',
  '/login': 'Anmelden',
  '/register': 'Registrieren',
  '/impressum': 'Impressum',
  '/datenschutz': 'Datenschutz',
  '/agb': 'AGB',
}

/** Oben steht nur die Seite, kein Pfad (Kim: "oben reicht Start"). */
export function seitentitel(pfad: string, istAdmin = false): string {
  const norm = normalisiert(pfad)
  if (!istAdmin && istAdminPfad(norm)) return 'Qualyz'
  if (TITEL[norm]) return TITEL[norm]
  const aktie = norm.match(/^\/aktie\/([^/]+)/)
  if (aktie) return decodeURIComponent(aktie[1]).toUpperCase()
  return 'Qualyz'
}
